import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Megaphone,
  Users,
  AlertTriangle,
  Crown,
  Cake,
  Heart,
  Gift,
  UserPlus,
  CalendarHeart,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Send,
  Download,
  Clock,
  IndianRupee,
  Star,
  TrendingUp,
  Calendar,
  Utensils,
  RefreshCw,
  Eye,
  CheckSquare,
  Square,
  Sparkles,
  Target,
  Zap,
  Phone,
  Mail,
  Building2,
} from "lucide-react";
import AdminLayout from "../../components/Admin/Layout/AdminLayout";
import ModuleLayout from "../../layouts/ModuleLayout";
import CustomerDetailsModal from "../../components/shared/CustomerDetailsModal";
import useCampaignCustomers from "@/hooks/useCampaignCustomers";
import useCustomerSegments from "@/hooks/useCustomerSegments";
import { useAuth } from "../../context/AuthContext";
import { P } from "@/config/permissionCodes";
import branchService from "../../services/branchService";

// ============================================
// CUSTOM ICONS - WhatsApp, SMS
// ============================================
const WhatsAppIcon = ({ size = 18, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const SMSIcon = ({ size = 18, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 10h.01" />
    <path d="M12 10h.01" />
    <path d="M16 10h.01" />
  </svg>
);


const normalizeCustomer = (c) => {
  const today = new Date();

  const lastVisit = c.lastVisit ? new Date(c.lastVisit) : null;
  const customerSince = c.customerSince ? new Date(c.customerSince) : null;

  const daysSinceLastVisit = lastVisit
    ? Math.floor((today - lastVisit) / (1000 * 60 * 60 * 24))
    : null;

  const customerType = (c.customerType ?? "").toLowerCase();

  return {
    ...c,

    birthday: c.birthday ? new Date(c.birthday) : null,
    anniversary: c.anniversary ? new Date(c.anniversary) : null,
    customerSince,
    joinDate: customerSince,
    tier:
    c.totalSpent >= 40000
      ? "Platinum"
      : c.totalSpent >= 25000
      ? "Gold"
      : c.totalSpent >= 10000
      ? "Silver"
      : "Bronze",

    lastVisit,
    lastVisitDays: daysSinceLastVisit,

    orderPreference: c.preferredOrderType ?? null,

    customerType,
    isVIP: customerType === "vip",
    isRegular: customerType === "regular",

    pointsExpiringSoon: c.expiryStatus === "expiring_soon",
  };
};

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const getUpcomingDate = (eventDate, baseDate = new Date()) => {
  if (!eventDate) return null;

  const upcoming = new Date(
    baseDate.getFullYear(),
    eventDate.getMonth(),
    eventDate.getDate()
  );

  if (upcoming < stripTime(baseDate)) {
    upcoming.setFullYear(upcoming.getFullYear() + 1);
  }

  return stripTime(upcoming);
};

const isInRange = (date, start, end) =>
  date >= stripTime(start) && date <= stripTime(end);


const getWeekStartMonday = (date) => {
  const d = stripTime(new Date(date));
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d;
};

const getWeekEndSunday = (start) => {
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
};


// ============================================
// MESSAGE TEMPLATES
// ============================================
const MESSAGE_TEMPLATES = [
  {
    id: "birthday",
    name: "Birthday Wishes",
    icon: Cake,
    color: "text-pink-500",
    bgColor: "bg-pink-50",
    message:
      "Dear {name}, wishing you a very Happy Birthday! 🎉 Celebrate with us and enjoy 20% OFF on your birthday meal. Valid for 7 days. Use code: BDAY{year}",
  },
  {
    id: "anniversary",
    name: "Anniversary Special",
    icon: Heart,
    color: "text-red-500",
    bgColor: "bg-red-50",
    message:
      "Dear {name}, congratulations on your anniversary! 💕 Make it special with a romantic dinner at our restaurant. Enjoy a complimentary dessert on us!",
  },
  {
    id: "miss-you",
    name: "We Miss You",
    icon: AlertTriangle,
    color: "text-amber-500",
    bgColor: "bg-amber-50",
    message:
      "Hi {name}, it's been a while since your last visit! We miss serving you. Here's 15% OFF on your next order. Use code: COMEBACK15",
  },
  {
    id: "points-expiring",
    name: "Points Expiring",
    icon: Clock,
    color: "text-orange-500",
    bgColor: "bg-orange-50",
    message:
      "Hi {name}, your {points} loyalty points are expiring in 7 days! Visit us soon to redeem them. Don't let them go to waste!",
  },
  {
    id: "vip-exclusive",
    name: "VIP Exclusive",
    icon: Crown,
    color: "text-purple-500",
    bgColor: "bg-purple-50",
    message:
      "Dear {name}, as our valued VIP customer, you're invited to an exclusive tasting event! RSVP now for a complimentary 5-course meal.",
  },
  {
    id: "new-menu",
    name: "New Menu Launch",
    icon: Sparkles,
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    message:
      "Hi {name}, we've added exciting new dishes to our menu! Be among the first to try them. Book your table now and get 10% OFF!",
  },
  {
    id: "thank-you",
    name: "Thank You",
    icon: Star,
    color: "text-green-500",
    bgColor: "bg-green-50",
    message:
      "Dear {name}, thank you for dining with us! We hope you enjoyed your meal. Rate us on Google and get 50 bonus loyalty points on your next visit!",
  },
  {
    id: "weekend-special",
    name: "Weekend Special",
    icon: Calendar,
    color: "text-indigo-500",
    bgColor: "bg-indigo-50",
    message:
      "Hi {name}, this weekend is special! Enjoy Buy 1 Get 1 Free on all starters. Valid Sat-Sun only. Reserve your table now!",
  },
];
const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay() || 7;

  const start = new Date(now);
  start.setDate(now.getDate() - day + 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const isDateInThisWeek = (date) => {
  if (!date) return false;

  const today = new Date();
  const { start, end } = getWeekRange();

  const compare = new Date(
    today.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  return compare >= start && compare <= end;
};

const isDateInLastNDays = (date, days = 30) => {
  if (!date) return false;

  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - days);

  return date >= past && date <= today;
};

// ============================================
// QUICK SEGMENTS CONFIGURATION
// ============================================
const QUICK_SEGMENTS = [
  {
    id: "at-risk",
    name: "At Risk",
    description: "No visit in 30+ days",
    icon: AlertTriangle,
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    countColor: "text-red-600",
    filter: (c) => !c.lastVisit || c.lastVisitDays > 60,
  },
  {
    id: "vip",
    name: "VIP Customers",
    description: "Top spenders (₹25K+)",
    icon: Crown,
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    countColor: "text-purple-600",
    filter: (c) => c.isVIP,
  },
  {
    id: "birthday-week",
    name: "Birthday This Week",
    description: "Upcoming birthdays",
    icon: Cake,
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200",
    iconBg: "bg-pink-100",
    iconColor: "text-pink-600",
    countColor: "text-pink-600",
    filter: (c) => isDateInThisWeek(c.birthday),
  },
  {
    id: "anniversary-week",
    name: "Anniversary This Week",
    description: "Upcoming anniversaries",
    icon: Heart,
    bgColor: "bg-rose-50",
    borderColor: "border-rose-200",
    iconBg: "bg-rose-100",
    iconColor: "text-rose-600",
    countColor: "text-rose-600",
    filter: (c) => isDateInThisWeek(c.anniversary),
  },
  {
    id: "points-expiring",
    name: "Points Expiring",
    description: "Expiring in 7 days",
    icon: Gift,
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    countColor: "text-orange-600",
    filter: (c) => c.pointsExpiringSoon === true,
  },
  {
    id: "new-customers",
    name: "New Customers",
    description: "Joined in last 30 days",
    icon: UserPlus,
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    countColor: "text-green-600",
    filter: (c) => {
      if (!c.joinDate) return false;
      if (c.totalVisits < 1) return false;
      return isDateInLastNDays(c.joinDate, 30);
    },
  },
  {
    id: "regulars",
    name: "Regulars",
    description: "20+ visits",
    icon: CalendarHeart,
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    countColor: "text-teal-600",
    filter: (c) => c.isRegular,
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const CampaignsPage = () => {
  const location = useLocation();
  const isCRMRoute = location.pathname.startsWith("/crm");
  const Layout = isCRMRoute ? ModuleLayout : AdminLayout;

  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // Permission flags
  const canView   = hasPermission(P.CRM.CAMPAIGNS.SCREEN);
  const canSend   = hasPermission(P.CRM.CAMPAIGNS.SEND);
  const canExport = hasPermission(P.CRM.CAMPAIGNS.EXPORT);

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [campaignResult, setCampaignResult] = useState(null); 
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  // State
  const raw = useCampaignCustomers(selectedBranchId) || {};
  const { sendCampaign } = raw;
  const customers = useMemo(
    () => (raw.customers || []).map(normalizeCustomer),
    [raw.customers]
  );

  const loading = raw.loading;
  const refetchCustomers = raw.refetch;

  const {
    segments,
    loading: segmentsLoading,
    refetch: refetchSegments,
  } = useCustomerSegments(selectedBranchId);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSegment, setActiveSegment] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageChannel, setMessageChannel] = useState("sms");
  const [customMessage, setCustomMessage] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Customer Details Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomerForView, setSelectedCustomerForView] = useState(null);

  // Advanced Filters State
  const [filters, setFilters] = useState({
    customerSince: "all",
    lastVisit: "all",
    spendingTier: "all",
    loyaltyPoints: "all",
    birthday: "all",
    anniversary: "all",
    orderPreference: "all",
  });

  // Filtered Customers
  const filteredCustomers = useMemo(() => {
    let result = [...customers];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.phone.includes(query) ||
          c.email.toLowerCase().includes(query)
      );
    }

    if (activeSegment) {
      const segment = QUICK_SEGMENTS.find((s) => s.id === activeSegment);
      if (segment) {
        result = result.filter(segment.filter);
      }
    }

    if (filters.customerSince !== "all") {
      const today = new Date();
      result = result.filter((c) => {
        const daysSinceJoined = (today - c.joinDate) / (1000 * 60 * 60 * 24);
        switch (filters.customerSince) {
          case "30days": return daysSinceJoined <= 30;
          case "3months": return daysSinceJoined <= 90;
          case "6months": return daysSinceJoined <= 180;
          case "1year": return daysSinceJoined <= 365;
          case "over1year": return daysSinceJoined > 365;
          default: return true;
        }
      });
    }

    if (filters.lastVisit !== "all") {
      switch (filters.lastVisit) {
        case "today": result = result.filter((c) => c.lastVisitDays === 0); break;
        case "week": result = result.filter((c) => c.lastVisitDays <= 7); break;
        case "month": result = result.filter((c) => c.lastVisitDays <= 30); break;
        case "3months": result = result.filter((c) => c.lastVisitDays <= 90); break;
        case "atRisk": result = result.filter((c) => c.lastVisitDays > 30); break;
        case "dormant": result = result.filter((c) => c.lastVisitDays > 60 && c.lastVisitDays <= 90); break;
        case "churned": result = result.filter((c) => c.lastVisitDays > 90); break;
      }
    }

    if (filters.spendingTier !== "all") {
      switch (filters.spendingTier) {
        case "platinum": result = result.filter((c) => c.totalSpent >= 40000); break;
        case "gold": result = result.filter((c) => c.totalSpent >= 25000 && c.totalSpent < 40000); break;
        case "silver": result = result.filter((c) => c.totalSpent >= 10000 && c.totalSpent < 25000); break;
        case "bronze": result = result.filter((c) => c.totalSpent < 10000); break;
      }
    }

    if (filters.loyaltyPoints !== "all") {
      switch (filters.loyaltyPoints) {
        case "expiring": result = result.filter((c) => c.pointsExpiringSoon > 0); break;
        case "high": result = result.filter((c) => c.loyaltyPoints >= 1000); break;
        case "medium": result = result.filter((c) => c.loyaltyPoints >= 500 && c.loyaltyPoints < 1000); break;
        case "low": result = result.filter((c) => c.loyaltyPoints > 0 && c.loyaltyPoints < 500); break;
        case "none": result = result.filter((c) => c.loyaltyPoints === 0); break;
      }
    }

    if (filters.birthday !== "all") {
      const today = stripTime(new Date());
      result = result.filter((c) => {
        if (!c.birthday) return false;
        const upcoming = getUpcomingDate(c.birthday, today);
        switch (filters.birthday) {
          case "today": return upcoming.getTime() === today.getTime();
          case "thisWeek": { const start = getWeekStartMonday(today); const end = getWeekEndSunday(start); return isInRange(upcoming, start, end); }
          case "nextWeek": { const start = getWeekStartMonday(today); start.setDate(start.getDate() + 7); const end = getWeekEndSunday(start); return isInRange(upcoming, start, end); }
          case "thisMonth": return upcoming.getMonth() === today.getMonth() && upcoming.getFullYear() === today.getFullYear();
          case "nextMonth": { const nextMonth = (today.getMonth() + 1) % 12; return upcoming.getMonth() === nextMonth; }
          default: return true;
        }
      });
    }

    if (filters.anniversary !== "all") {
      const today = stripTime(new Date());
      result = result.filter((c) => {
        if (!c.anniversary) return filters.anniversary === "none";
        const upcoming = getUpcomingDate(c.anniversary, today);
        switch (filters.anniversary) {
          case "today": return upcoming.getTime() === today.getTime();
          case "thisWeek": { const start = getWeekStartMonday(today); const end = getWeekEndSunday(start); return isInRange(upcoming, start, end); }
          case "nextWeek": { const start = getWeekStartMonday(today); start.setDate(start.getDate() + 7); const end = getWeekEndSunday(start); return isInRange(upcoming, start, end); }
          case "thisMonth": return upcoming.getMonth() === today.getMonth() && upcoming.getFullYear() === today.getFullYear();
          case "nextMonth": return upcoming.getMonth() === (today.getMonth() + 1) % 12;
          case "none": return false;
          default: return true;
        }
      });
    }

    if (filters.orderPreference !== "all") {
      result = result.filter((c) => c.orderPreference === filters.orderPreference);
    }

    return result;
  }, [customers, searchQuery, activeSegment, filters]);

  const handleSelectAll = () => {
    if (selectedCustomers.size === filteredCustomers.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(filteredCustomers.map((c) => c.id)));
    }
  };

  const handleSelectCustomer = (customerId) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedCustomers(newSelected);
  };

  const handleSendMessage = (channel, customer = null) => {
    // Guard: must have SEND permission
    if (!canSend) {
      return;
    }
    setMessageChannel(channel);
    if (customer) {
      setSelectedCustomers(new Set([customer.id]));
    }
    setShowMessageModal(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const start = Date.now();
    try {
      await Promise.all([refetchCustomers?.(), refetchSegments?.()]);
    } finally {
      const elapsed = Date.now() - start;
      const minDuration = 40;
      setTimeout(() => setIsRefreshing(false), Math.max(0, minDuration - elapsed));
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomerForView({
      id: customer.id,
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      loyaltyPoints: customer.loyaltyPoints,
      preferredPaymentMode: customer.preferredPaymentMode ?? "—",
      dateOfBirth: customer.birthday ? customer.birthday.toISOString() : null,
      anniversaryDate: customer.anniversary ? customer.anniversary.toISOString() : null,
      createdAt: customer.joinDate ? customer.joinDate.toISOString() : null,
    });
    setShowDetailModal(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomMessage(template.message);
  };

const handleSendCampaign = async () => {
  if (!customMessage.trim() || selectedCustomers.size === 0) return;

  setIsSending(true);
  try {
    const payload = {
      channel: messageChannel.toUpperCase(), // IMPORTANT
      customerIds: Array.from(selectedCustomers),
      message: customMessage.trim(),
      template: selectedTemplate?.id?.toUpperCase() || null, // 🔥 ADD THIS
    };

    const res = await sendCampaign(payload);
    const result = res?.data ?? res;

    setCampaignResult({
      sent: result.sent ?? 0,
      failed: result.failed ?? 0,
      total: result.total ?? selectedCustomers.size,
    });

    setTimeout(() => {
      setShowMessageModal(false);
      setCampaignResult(null);
      setSelectedCustomers(new Set());
      setCustomMessage("");
      setSelectedTemplate(null);
    }, 2000); 

  } catch (err) {
    setCampaignResult({ error: true }); 
  } finally {
    setIsSending(false);
  }
};

  const clearFilters = () => {
    setActiveSegment(null);
    setSearchQuery("");
    setFilters({
      customerSince: "all",
      lastVisit: "all",
      spendingTier: "all",
      loyaltyPoints: "all",
      birthday: "all",
      anniversary: "all",
      orderPreference: "all",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "Platinum": return "bg-gradient-to-r from-violet-600 to-purple-700 text-white";
      case "Gold": return "bg-gradient-to-r from-yellow-500 to-amber-600 text-white";
      case "Silver": return "bg-gradient-to-r from-slate-400 to-slate-500 text-white";
      default: return "bg-gradient-to-r from-amber-700 to-orange-800 text-white";
    }
  };

  const segmentCountMap = {
    "at-risk": segments.atRisk,
    vip: segments.vips,
    "birthday-week": segments.birthdayThisWeek,
    "anniversary-week": segments.anniversaryThisWeek,
    "points-expiring": segments.pointsExpiring,
    "new-customers": segments.newCustomers,
    regulars: segments.regulars,
  };

  const handleExportCsv = () => {
    // Guard: must have EXPORT permission
    if (!canExport) return;
    if (!filteredCustomers.length) return;

    const headers = ["Name", "Phone", "Email", "Tier", "Total Visits", "Total Spent", "Loyalty Points", "Last Visit (Days)", "Birthday", "Anniversary"];
    const rows = filteredCustomers.map((c) => [
      c.name, c.phone, c.email, c.tier, c.totalVisits, c.totalSpent,
      c.loyaltyPoints, c.lastVisitDays,
      c.birthday ? formatDate(c.birthday) : "",
      c.anniversary ? formatDate(c.anniversary) : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${val ?? ""}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `campaign-customers-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Guard: no view permission ───────────────────────────────────────────────
  if (!canView) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full text-center px-6 py-20">
          <div>
            <p className="text-lg font-semibold text-gray-700">Access Denied</p>
            <p className="text-sm text-gray-500 mt-1">You don't have permission to view campaigns.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 bg-gradient-to-br from-rose-50/30 via-white to-pink-50/20 min-h-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl shadow-lg">
                <Megaphone className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Campaigns</h1>
                <p className="text-gray-500 text-xs">Engage customers with targeted promotions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Branch Dropdown for SUPER_USER */}
              {branches.length > 1 && (
                <div className="relative">
                  <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select
                    value={selectedBranchId ?? ''}
                    onChange={(e) => setSelectedBranchId(e.target.value || null)}
                    className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                  >
                    <option value="">All Branches</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              )}

              {/* Refresh — always visible */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || loading}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
                Refresh
              </button>

              {/* Export — only if canExport */}
              {canExport && (
                <button
                  onClick={handleExportCsv}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 text-sm transition-colors"
                >
                  <Download size={16} />
                  Export
                </button>
              )}

              {/* Send Campaign — only if canSend */}
              {canSend && (
                <button
                  onClick={() => handleSendMessage("sms")}
                  disabled={selectedCustomers.size === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm"
                >
                  <Send size={16} />
                  Send Campaign ({selectedCustomers.size})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Segments */}
        <div className="mb-5">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Target size={14} />
            Quick Segments
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {QUICK_SEGMENTS.map((segment) => {
              const Icon = segment.icon;
              const count = segmentCountMap[segment.id] ?? 0;
              const isActive = activeSegment === segment.id;

              return (
                <button
                  key={segment.id}
                  onClick={() => setActiveSegment(isActive ? null : segment.id)}
                  className={`relative p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                    isActive
                      ? `${segment.bgColor} ${segment.borderColor} shadow-sm`
                      : "bg-white border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`p-1.5 rounded-md ${segment.iconBg}`}>
                      <Icon size={14} className={segment.iconColor} />
                    </div>
                    <span className={`text-lg font-bold ${segment.countColor}`}>{count}</span>
                  </div>
                  <div className="font-medium text-gray-900 text-xs">{segment.name}</div>
                  <div className="text-[10px] text-gray-400">{segment.description}</div>
                  {isActive && (
                    <div className={`absolute -top-1 -right-1 w-4 h-4 ${segment.iconBg} rounded-full flex items-center justify-center`}>
                      <Check size={10} className={segment.iconColor} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none text-sm"
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
                showFilters
                  ? "bg-rose-50 border-rose-200 text-rose-700"
                  : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Filter size={14} />
              Filters
              {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {(activeSegment || searchQuery || Object.values(filters).some((v) => v !== "all")) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                <RefreshCw size={14} />
                Clear
              </button>
            )}

            <div className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">{filteredCustomers.length}</span> customers
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Customer Since</label>
                  <select value={filters.customerSince} onChange={(e) => setFilters({ ...filters, customerSince: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">All Time</option>
                    <option value="30days">Last 30 Days</option>
                    <option value="3months">Last 3 Months</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="1year">Last 1 Year</option>
                    <option value="over1year">Over 1 Year</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Last Visit</label>
                  <select value={filters.lastVisit} onChange={(e) => setFilters({ ...filters, lastVisit: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">Any Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="3months">Last 3 Months</option>
                    <option value="atRisk">At Risk (30-60 days)</option>
                    <option value="dormant">Dormant (60-90 days)</option>
                    <option value="churned">Churned (90+ days)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Spending Tier</label>
                  <select value={filters.spendingTier} onChange={(e) => setFilters({ ...filters, spendingTier: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">All Tiers</option>
                    <option value="platinum">Platinum (₹40K+)</option>
                    <option value="gold">Gold (₹25K-40K)</option>
                    <option value="silver">Silver (₹10K-25K)</option>
                    <option value="bronze">Bronze (&lt;₹10K)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Loyalty Points</label>
                  <select value={filters.loyaltyPoints} onChange={(e) => setFilters({ ...filters, loyaltyPoints: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">All</option>
                    <option value="expiring">⚠️ Expiring Soon</option>
                    <option value="high">High (1000+)</option>
                    <option value="medium">Medium (500-999)</option>
                    <option value="low">Low (1-499)</option>
                    <option value="none">No Points</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Birthday</label>
                  <select value={filters.birthday} onChange={(e) => setFilters({ ...filters, birthday: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">Any</option>
                    <option value="today">Today</option>
                    <option value="thisWeek">This Week</option>
                    <option value="nextWeek">Next Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="nextMonth">Next Month</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Anniversary</label>
                  <select value={filters.anniversary} onChange={(e) => setFilters({ ...filters, anniversary: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">Any</option>
                    <option value="today">Today</option>
                    <option value="thisWeek">This Week</option>
                    <option value="nextWeek">Next Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="nextMonth">Next Month</option>
                    <option value="none">No Anniversary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Order Type</label>
                  <select value={filters.orderPreference} onChange={(e) => setFilters({ ...filters, orderPreference: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-rose-500">
                    <option value="all">All Types</option>
                    <option value="DINE_IN">Dine-in</option>
                    <option value="TAKE_AWAY">Takeaway</option>
                    <option value="DELIVERY">Delivery</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar — only shown if canSend and something is selected */}
        {selectedCustomers.size > 0 && canSend && (
          <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-lg p-3 mb-4 flex items-center justify-between text-white shadow-lg">
            <div className="flex items-center gap-2">
              <CheckSquare size={18} />
              <span className="font-medium text-sm">{selectedCustomers.size} selected</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => handleSendMessage("sms")} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm">
                <SMSIcon size={16} /> SMS
              </button>
              <button onClick={() => handleSendMessage("whatsapp")} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm">
                <WhatsAppIcon size={16} /> WhatsApp
              </button>
              <button onClick={() => handleSendMessage("email")} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm">
                <Mail size={16} /> Email
              </button>
              <div className="w-px h-5 bg-white/30 mx-1" />
              <button onClick={() => setSelectedCustomers(new Set())} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Customer List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden relative">
          {(loading || isRefreshing) && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center z-20 pointer-events-auto">
              <RefreshCw size={28} className="text-rose-500 animate-spin" />
            </div>
          )}

          {/* Table Header */}
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
            {/* Checkbox — only show if canSend (selecting customers is only useful for sending) */}
            {canSend && (
              <button onClick={handleSelectAll} className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0">
                {selectedCustomers.size === filteredCustomers.length && filteredCustomers.length > 0 ? (
                  <CheckSquare size={18} className="text-rose-600" />
                ) : (
                  <Square size={18} className="text-gray-400" />
                )}
              </button>
            )}

            <div className="w-10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</span>
            </div>
            <div className="hidden lg:flex items-center gap-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <div className="w-12 text-center">Visits</div>
              <div className="w-16 text-center">Spent</div>
              <div className="w-12 text-center">Points</div>
              <div className="w-16 text-center">Last Visit</div>
            </div>
            <div className="hidden md:flex items-center gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
              <div className="w-16 text-center">Birthday</div>
              <div className="w-16 text-center">Anniv.</div>
            </div>
            <div className="w-36 flex-shrink-0 text-right">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</span>
            </div>
          </div>

          {/* Customer Rows */}
          <div className="divide-y divide-gray-100">
            {filteredCustomers.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No customers found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`px-4 py-2.5 hover:bg-gray-50/50 transition-colors ${selectedCustomers.has(customer.id) ? "bg-rose-50/30" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Checkbox — only if canSend */}
                    {canSend && (
                      <button onClick={() => handleSelectCustomer(customer.id)} className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0">
                        {selectedCustomers.has(customer.id) ? (
                          <CheckSquare size={18} className="text-rose-600" />
                        ) : (
                          <Square size={18} className="text-gray-300" />
                        )}
                      </button>
                    )}

                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {customer.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>

                    {/* Customer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{customer.name}</h3>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTierColor(customer.tier)}`}>{customer.tier}</span>
                        {customer.isVIP && <Crown size={12} className="text-purple-500 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1 truncate"><Phone size={11} className="flex-shrink-0" />{customer.phone}</span>
                        <span className="hidden xl:flex items-center gap-1 truncate"><Mail size={11} className="flex-shrink-0" />{customer.email}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="hidden lg:flex items-center gap-6 text-xs">
                      <div className="w-12 text-center font-semibold text-gray-900">{customer.totalVisits}</div>
                      <div className="w-16 text-center font-semibold text-gray-900">{formatCurrency(customer.totalSpent)}</div>
                      <div className="w-12 text-center font-semibold text-gray-900">{customer.loyaltyPoints}</div>
                      <div className={`w-16 text-center font-semibold ${customer.lastVisitDays > 30 ? "text-red-500" : "text-gray-900"}`}>
                        {customer.lastVisitDays === 0 ? "Today" : `${customer.lastVisitDays}d`}
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0">
                      <div className="w-16 text-center">
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-pink-50 text-pink-600 rounded">
                          <Cake size={10} />{formatDate(customer.birthday)}
                        </span>
                      </div>
                      <div className="w-16 text-center">
                        {customer.anniversary ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded">
                            <Heart size={10} />{formatDate(customer.anniversary)}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="w-36 flex items-center justify-end gap-0.5 flex-shrink-0">
                      {/* Send buttons — only if canSend */}
                      {canSend && (
                        <>
                          <button onClick={() => handleSendMessage("sms", customer)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Send SMS">
                            <SMSIcon size={15} />
                          </button>
                          <button onClick={() => handleSendMessage("whatsapp", customer)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Send WhatsApp">
                            <WhatsAppIcon size={15} />
                          </button>
                          <button onClick={() => handleSendMessage("email", customer)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Send Email">
                            <Mail size={15} />
                          </button>
                        </>
                      )}
                      {/* View — always visible */}
                      <button onClick={() => handleViewCustomer(customer)} className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="View Profile">
                        <Eye size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Modal — only accessible if canSend */}
        {showMessageModal && canSend && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-rose-500 to-pink-600 text-white">
                <div className="flex items-center gap-3">
                  <Send size={20} />
                  <div>
                    <h2 className="font-semibold">Send Campaign</h2>
                    <p className="text-xs text-white/80">{selectedCustomers.size} customer{selectedCustomers.size !== 1 ? "s" : ""} selected</p>
                  </div>
                </div>
                <button onClick={() => { setShowMessageModal(false); setCampaignResult(null); setCustomMessage(""); setSelectedTemplate(null); }} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 overflow-y-auto max-h-[calc(85vh-160px)]">
                {/* Channel Selection */}
                <div className="mb-5">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Select Channel</label>
                  <div className="flex gap-2">
                    {["sms", "whatsapp", "email"].map((channel) => (
                      <button
                        key={channel}
                        onClick={() => setMessageChannel(channel)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition-all text-sm ${
                          messageChannel === channel
                            ? channel === "email" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-green-500 bg-green-50 text-green-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {channel === "sms" && <><SMSIcon size={18} /> SMS</>}
                        {channel === "whatsapp" && <><WhatsAppIcon size={18} /> WhatsApp</>}
                        {channel === "email" && <><Mail size={18} /> Email</>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template Selection */}
                <div className="mb-5">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Choose Template</label>
                  <div className="grid grid-cols-4 gap-2">
                    {MESSAGE_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                          selectedTemplate?.id === template.id
                            ? "border-rose-500 bg-rose-50 shadow-sm"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className={`p-1.5 rounded ${template.bgColor} w-fit mb-1.5`}>
                          <template.icon size={14} className={template.color} />
                        </div>
                        <div className="font-medium text-xs text-gray-900 truncate">{template.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Message</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Write your message here... Use {name}, {points}, {year} for personalization"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none resize-none text-sm"
                    rows={4}
                  />
                  <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                    <span>Variables: {"{name}"}, {"{points}"}, {"{year}"}</span>
                    <span>{customMessage.length} chars</span>
                  </div>
                </div>

                {customMessage && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-[10px] font-medium text-gray-400 uppercase mb-1">Preview</div>
                    <p className="text-sm text-gray-700">
                      {customMessage.replace("{name}", "Rahul").replace("{points}", "500").replace("{year}", "2024")}
                    </p>
                  </div>
                )}
              </div>

              {/* Campaign Result Banner */}
{campaignResult && (
  <div className={`mx-5 mb-3 p-3 rounded-lg border text-sm ${
    campaignResult.error
      ? "bg-red-50 border-red-200 text-red-700"
      : campaignResult.failed > 0
      ? "bg-amber-50 border-amber-200 text-amber-700"
      : "bg-green-50 border-green-200 text-green-700"
  }`}>
    {campaignResult.error ? (
      <div className="flex items-center gap-2">
        <X size={16} /> Failed to send campaign. Please try again.
      </div>
    ) : (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Check size={16} />
          <span>
            Sent to <strong>{campaignResult.sent}</strong> of{" "}
            <strong>{campaignResult.total}</strong> customers
            {campaignResult.failed > 0 && (
              <span className="ml-1 text-amber-600">
                · {campaignResult.failed} failed
              </span>
            )}
          </span>
        </div>
        {campaignResult.failed === 0 && (
          <button
            onClick={() => { setShowMessageModal(false); setCampaignResult(null); }}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            Close
          </button>
        )}
      </div>
    )}
  </div>
)}

{/* Footer */}
<div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
  <button
    onClick={() => { setShowMessageModal(false); setCampaignResult(null); setCustomMessage(""); setSelectedTemplate(null); }}
    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
  >
    Cancel
  </button>
  <button
    onClick={handleSendCampaign}
    disabled={!customMessage.trim() || isSending || !!campaignResult?.sent}
    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow text-sm"
  >
    {isSending ? (
      <><RefreshCw size={16} className="animate-spin" /> Sending...</>
    ) : (
      <><Zap size={16} /> Send to {selectedCustomers.size}</>
    )}
  </button>
</div>
            </div>
          </div>
        )}

        {/* Customer Details Modal */}
        <CustomerDetailsModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          customer={selectedCustomerForView}
          showEditButton={false}
        />
      </div>
    </Layout>
  );
};

export default CampaignsPage;